import React, { useState, useEffect, useCallback } from 'react';
import { reportService, extractErrorDetails } from '../services/api';
import ErrorDisplay from './ErrorDisplay';

function LowStockReport() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [errorTrace, setErrorTrace] = useState(null);
  const [threshold, setThreshold] = useState('10');
  const [appliedThreshold, setAppliedThreshold] = useState('10');

  const fetchLowStockProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setErrorTrace(null);
      const thresholdValue = appliedThreshold ? parseInt(appliedThreshold, 10) : null;
      const response = await reportService.getLowStockProducts(thresholdValue);
      if (response.success) {
        setProducts(response.data || []);
      } else {
        setError(response.message || 'Error al cargar el reporte');
        setErrorTrace(response.errorTrace || null);
      }
    } catch (err) {
      const { message, errorTrace: trace } = extractErrorDetails(err, 'Error al cargar el reporte de bajo stock');
      setError(message);
      setErrorTrace(trace);
    } finally {
      setLoading(false);
    }
  }, [appliedThreshold]);

  useEffect(() => {
    fetchLowStockProducts();
  }, [fetchLowStockProducts]);

  const handleApplyFilter = () => {
    setAppliedThreshold(threshold);
  };

  const handleClearFilter = () => {
    setThreshold('');
    setAppliedThreshold('');
  };

  const getStockClass = (stock) => {
    if (stock <= 0) return 'stock-critical';
    if (stock < 5) return 'stock-low';
    if (stock < 10) return 'stock-warning';
    return 'stock-ok';
  };

  return (
    <div className="report-container">
      <h2>Reporte de Productos con Bajo Stock</h2>
      
      <div className="report-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label htmlFor="threshold">Mostrar productos con stock menor a:</label>
            <input
              type="number"
              id="threshold"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              placeholder="Ej: 10"
              min="0"
            />
          </div>
          <div className="filter-actions">
            <button onClick={handleApplyFilter} className="btn btn-primary">
              Aplicar Filtro
            </button>
            <button onClick={handleClearFilter} className="btn btn-secondary">
              Ver Todos
            </button>
          </div>
        </div>
        <div className="export-buttons" style={{ marginTop: '10px' }}>
          <button
            onClick={async () => {
              try {
                const thresholdValue = appliedThreshold ? parseInt(appliedThreshold, 10) : null;
                await reportService.exportLowStockToPdf(thresholdValue);
              } catch (err) {
                console.error('Error exporting to PDF:', err);
                alert('Error al exportar a PDF');
              }
            }}
            className="btn btn-small btn-primary"
            style={{ marginRight: '10px' }}
          >
            📄 Exportar a PDF
          </button>
          <button
            onClick={async () => {
              try {
                const thresholdValue = appliedThreshold ? parseInt(appliedThreshold, 10) : null;
                await reportService.exportLowStockToExcel(thresholdValue);
              } catch (err) {
                console.error('Error exporting to Excel:', err);
                alert('Error al exportar a Excel');
              }
            }}
            className="btn btn-small btn-success"
            style={{ marginRight: '10px' }}
          >
            📊 Exportar a Excel
          </button>
          <button
            onClick={async () => {
              try {
                const thresholdValue = appliedThreshold ? parseInt(appliedThreshold, 10) : null;
                await reportService.exportLowStockToCsv(thresholdValue);
              } catch (err) {
                console.error('Error exporting to CSV:', err);
                alert('Error al exportar a CSV');
              }
            }}
            className="btn btn-small btn-info"
          >
            📋 Exportar a CSV
          </button>
        </div>
      </div>

      <ErrorDisplay error={error} errorTrace={errorTrace} />

      {loading ? (
        <p className="loading-text">Cargando productos...</p>
      ) : (
        <>
          <div className="report-summary">
            <p>
              {appliedThreshold 
                ? `Mostrando ${products.length} productos con stock menor a ${appliedThreshold} unidades`
                : `Mostrando ${products.length} productos ordenados por stock`}
            </p>
          </div>

          {products.length === 0 ? (
            <div className="empty-state">
              <p>No hay productos que coincidan con el criterio de búsqueda</p>
            </div>
          ) : (
            <div className="report-table-container">
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Código de Barras</th>
                    <th>Nombre</th>
                    <th>Descripción</th>
                    <th>Stock Actual</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => (
                    <tr key={product.id} className={getStockClass(product.stock)}>
                      <td>{product.codigoBarras}</td>
                      <td>{product.nombre}</td>
                      <td>{product.descripcion || '-'}</td>
                      <td className="stock-cell">
                        <span className={`stock-badge ${getStockClass(product.stock)}`}>
                          {product.stock}
                        </span>
                      </td>
                      <td>
                        {product.stock <= 0 && <span className="status-critical">Sin Stock</span>}
                        {product.stock > 0 && product.stock < 5 && <span className="status-low">Muy Bajo</span>}
                        {product.stock >= 5 && product.stock < 10 && <span className="status-warning">Bajo</span>}
                        {product.stock >= 10 && <span className="status-ok">Normal</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default LowStockReport;
